import { DatosMonitoreo, LoteMonitoreado } from "../agruparPorSemana";
/** Lote del catálogo que viaja en la raíz del dashboard. El nombre se manda una
 *  sola vez acá y las sesiones lo referencian por id, así renombrar un lote no
 *  parte su serie histórica en dos. */
export type SesionDashboardLote = {
  /** Id real del lote (calza con `ClienteFincaLoteResponse.id`). */
  id: string;
  /** Nombre del lote, para mostrar. */
  nombre: string;
};

/** Una sesión de monitoreo dentro del histórico del dashboard. Es la forma
 *  liviana de la sesión: sin el árbol crudo de árboles/detecciones que trae
 *  `…/list`, y con la `semana` ISO y las incidencias ya calculadas por el
 *  server. */
export type SesionDashboardItem = {
  /** Id de la sesión (el que pide `…/{sesionId}/detalle`). */
  id: string;
  /** Semana ISO 8601 del año (semana 1 = la del primer jueves de enero). */
  semana: number;
  /** Fecha de la sesión (`yyyy-MM-dd`). */
  fecha: string;
  /** La sesión fue cerrada por el monitor. Las abiertas también llegan: el
   *  frontend las incluye y las marca. */
  isFinalizada: boolean;
  /** Incidencias de la sesión: `datos[loteId][plagaEnfermedadId]` = promedio de
   *  conteo por árbol (Σcantidad / nArboles), ya calculado por el server.
   *
   *  **Un lote presente acá fue monitoreado**, aunque su mapa de plagas venga
   *  vacío (`{}`); un lote **ausente** no se monitoreó esa semana. Nunca leer
   *  este campo a mano: usar `leerIncidencia`, que codifica esa distinción. */
  datos: DatosMonitoreo;
};

/** Respuesta de `GET monitoreos/sesiones/finca/byFincaId/{fincaId}/dashboard`:
 *  el histórico de sesiones de la finca, más el catálogo de lotes que aparecen
 *  en el rango. No es un listado sincronizable — no lleva `ultimaSincronizacion`
 *  y no devuelve deltas — por eso no tiene store en IndexedDB ni pasa por
 *  `BaseGetListService`. Acepta filtros opcionales de rango (`anio`, o
 *  `desde`/`hasta`). */
export type SesionDashboardResponse = {
  /** Todos los lotes que aparecen en el rango consultado. */
  lotes: SesionDashboardLote[];
  /** Sesiones del histórico. */
  sesiones: SesionDashboardItem[];
};

/**
 * Lee una incidencia distinguiendo los dos casos que el contrato separa.
 *
 * Toma cualquier cosa que tenga `datos` con esa forma: sirve tanto para una
 * sesión suelta como para una `SemanaMonitoreo` ya agrupada, que conserva la
 * misma semántica.
 *
 *
 * - lote **ausente** de `datos` → no se monitoreó esa semana → `null`, que el
 *   gráfico dibuja como hueco en la curva.
 * - lote **presente** pero sin esa plaga → se monitoreó y no se detectó nada →
 *   `0`.
 *
 * La diferencia es agronómica, no cosmética: una plaga que bajó a cero es una
 * buena noticia y tiene que verse tocando el eje, no como un tramo faltante
 * que se lee como "dejamos de medir".
 */
export function leerIncidencia(
  origen: { datos: DatosMonitoreo },
  loteId: string,
  plagaId: string,
): number | null {
  const porLote = origen.datos?.[loteId];
  if (porLote === undefined) return null;
  return porLote.inc[plagaId] ?? 0;
}

/**
 * Árboles del lote con la plaga presente, con la misma distinción que
 * {@link leerIncidencia}: lote ausente → `null`; lote presente sin la plaga → 0.
 *
 * Es el numerador de la **incidencia** (`afectados / muestra × 100`), que es la
 * métrica contra la que el informe de Campoalegre compara el umbral. Todavía no
 * la consume el semáforo: eso es el motor de evaluación (fase 2).
 */
export function leerAfectados(
  origen: { datos: DatosMonitoreo },
  loteId: string,
  plagaId: string,
): number | null {
  const porLote = origen.datos?.[loteId];
  if (porLote === undefined) return null;
  return porLote.afectados[plagaId] ?? 0;
}

/**
 * El lote completo de una semana, para evaluarlo con el motor
 * (especificación v1.0, 5.4: reemplaza a {@link leerIncidencia}).
 *
 * Conserva la misma distinción que las dos funciones de arriba, que es la que
 * importa: lote **ausente** → `null`, no se monitoreó; lote **presente** → se
 * monitoreó, y una plaga que no esté en su mapa vale 0.
 *
 * Devuelve el lote entero y no un valor suelto porque el semáforo necesita las
 * tres cosas juntas —promedio, afectados y muestra— más la referencia congelada:
 * con el promedio solo no se puede calcular la incidencia ni saber contra qué
 * umbral compararlo.
 */
export function leerResultado(
  origen: { datos: DatosMonitoreo },
  loteId: string,
): LoteMonitoreado | null {
  return origen.datos?.[loteId] ?? null;
}
